import React from 'react';
import { MessageCircle, Camera, Check, ChevronRight, Clock, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatWhatsAppLink, getPhotoIdentificationMessage } from '../utils/whatsapp';
import { useSiteData } from '../contexts/SiteDataContext';

const HERO_TRACTOR_BG = "https://static.prod-images.emergentagent.com/jobs/c5896361-7756-4d42-a00f-bfd80f452324/images/b6d39005f4c611ea28364987c3cc2fea3372f73f5db9248145556d9fef641718.png";

const Hero = () => {
  const { pageContents } = useSiteData();
  const hero = pageContents.hero || {};
  const meta = hero.metadata || {};
  const bgImage = meta.backgroundImage || meta.imageUrl || HERO_TRACTOR_BG;

  return (
    <section data-testid="hero-section" className="relative w-full overflow-hidden bg-[#0a1a0b]" style={{ minHeight: '560px' }}>
      {/* Background: tractor very subtle */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="TerraHydro - Fundo agricola"
          className="w-full h-full object-cover opacity-[0.18]"
          style={{ objectPosition: '60% 40%', filter: 'blur(1.5px) saturate(0.7)' }}
        />
        {/* Subtle gradient to keep readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1a0b]/60 via-transparent to-[#0a1a0b]/80" />
      </div>

      {/* Decorative subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Main content - centered, well distributed */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center py-20 md:py-28">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8 border border-[#FFB025]/25" style={{ background: 'rgba(255,176,37,0.08)' }}>
            <div className="w-2 h-2 rounded-full bg-[#FFB025] animate-pulse" />
            <span className="text-[#FFB025] font-semibold text-sm tracking-wide">
              {hero.subtitle || 'Especialistas em Hidr\u00e1ulica'}
            </span>
          </div>

          {/* Title */}
          <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-white mb-6 max-w-3xl">
            {hero.title
              ? hero.title.split(',').map((part, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {i > 0 ? <span className="text-[#FFB025]">{part}</span> : part}
                    {i === 0 && ','}
                  </span>
                ))
              : <>{'A pe\u00e7a certa,'}<br /><span className="text-[#FFB025]">{'na aplica\u00e7\u00e3o correta.'}</span></>
            }
          </h1>

          {/* Description - UPDATED TEXT */}
          <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-8 max-w-2xl">
            {'Especialistas em componentes hidr\u00e1ulicos originais para linha agr\u00edcola e industrial. Bombas, motores, comandos e pe\u00e7as com diagn\u00f3stico t\u00e9cnico preciso e envio r\u00e1pido.'}
          </p>

          {/* Highlights - UPDATED: only "Peças originais" */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-10">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Check size={14} className="text-[#FFB025] flex-shrink-0" />
              <span>{'Pe\u00e7as originais'}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Check size={14} className="text-[#FFB025] flex-shrink-0" />
              <span>{'Diagn\u00f3stico t\u00e9cnico preciso'}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Check size={14} className="text-[#FFB025] flex-shrink-0" />
              <span>{'Envio r\u00e1pido para todo o Brasil e Exterior'}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-cta-specialist"
              size="lg"
              onClick={() => window.open(formatWhatsAppLink(), '_blank')}
              className="bg-[#FFB025] text-[#0a1a0b] hover:bg-[#ffc04d] font-bold text-base px-10 py-6 rounded-lg transition-all duration-300 shadow-lg shadow-[#FFB025]/15 hover:shadow-xl hover:shadow-[#FFB025]/25 hover:-translate-y-0.5"
            >
              <MessageCircle className="mr-2" size={20} />
              Falar com Especialista
              <ChevronRight className="ml-1" size={16} />
            </Button>
            <Button
              data-testid="hero-cta-photo"
              size="lg"
              variant="outline"
              onClick={() => window.open(formatWhatsAppLink(getPhotoIdentificationMessage()), '_blank')}
              className="border-2 border-white/20 text-white hover:bg-white hover:text-[#0a1a0b] font-semibold text-base px-10 py-6 rounded-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              <Camera className="mr-2" size={20} />
              {'Enviar Foto da Pe\u00e7a'}
            </Button>
          </div>

          {/* Stats/Trust bar - UPDATED */}
          <div className="mt-14 pt-8 border-t border-white/10 w-full max-w-3xl">
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFB025]/10 flex items-center justify-center">
                  <Clock size={18} className="text-[#FFB025]" />
                </div>
                <span className="text-white font-bold text-lg">15+</span>
                <span className="text-gray-500 text-xs">{'Anos de Experi\u00eancia'}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFB025]/10 flex items-center justify-center">
                  <Shield size={18} className="text-[#FFB025]" />
                </div>
                <span className="text-white font-bold text-lg">98%</span>
                <span className="text-gray-500 text-xs">{'Satisfa\u00e7\u00e3o dos Clientes'}</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FFB025]/10 flex items-center justify-center">
                  <Truck size={18} className="text-[#FFB025]" />
                </div>
                <span className="text-white font-bold text-lg">Brasil e Exterior</span>
                <span className="text-gray-500 text-xs">Despacho no mesmo dia</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FFB025]/40 to-transparent" />
    </section>
  );
};

export default Hero;
